import { useEffect } from "react";
import { siteConfig } from "../../data/siteConfig";

type PageMetaProps = {
  title: string;
  description: string;
  path: string;
  includeBusinessSchema?: boolean;
};

function setMeta(selector: string, attribute: "name" | "property", value: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.content = content;
}

export function PageMeta({ title, description, path, includeBusinessSchema = false }: PageMetaProps) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    const baseUrl = siteConfig.domain || window.location.origin;
    const canonicalUrl = `${baseUrl}${path === "/" ? "" : path}`;
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);

    const schemaId = "casagrande-local-business";
    document.getElementById(schemaId)?.remove();
    if (includeBusinessSchema) {
      const schema = document.createElement("script");
      schema.id = schemaId;
      schema.type = "application/ld+json";
      schema.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Bakery",
        name: siteConfig.businessName,
        telephone: siteConfig.phoneDisplay,
        areaServed: {
          "@type": "City",
          name: siteConfig.city,
        },
        ...(siteConfig.domain ? { url: siteConfig.domain } : {}),
      });
      document.head.appendChild(schema);
    }

    return () => {
      document.getElementById(schemaId)?.remove();
    };
  }, [description, includeBusinessSchema, path, title]);

  return null;
}
